import type { MachineContext } from 'typings/app/machineContext'
import { isBedbankItem, isInstantBookingLEHotelItem } from 'lib/checkout/checkoutUtils'
import { isVisibleUserCartMultiCart } from './multiCartVisibility'
import { encodeSearchParams } from 'lib/search/searchUtils'
import { addQueryParamsToPath, deleteSearchParams, getQueryParam } from 'lib/url/searchUrlUtils'
import { unique } from 'lib/array/arrayUtils'

const REPLACE_CART_ITEM_ID = 'replaceCartItemId'

export function getCartAvailabilityLink(offerLink: string, items: Array<App.MultiCartAccommodationItem>): string {
  if (!items.length) return offerLink
  // Encode the source rooms, including saved-for-later rooms omitted from the normal tile link.
  const occupancyParams = encodeSearchParams({ rooms: items.map(item => item.occupancy) })
  return addQueryParamsToPath(offerLink, {
    ...Object.fromEntries(occupancyParams),
    [REPLACE_CART_ITEM_ID]: items.map(item => item.id).join(','),
  })
}

export function getCartAvailabilityReplacement(
  search: string,
  context: MachineContext.MultiCartMachineContext,
  newItems: Array<App.Checkout.AnyItem>,
) {
  const cartId = context.activeCartId
  const itemIds = unique((getQueryParam(search, REPLACE_CART_ITEM_ID) ?? '').split(',').filter(Boolean))
  if (!cartId || !itemIds.length || context.cartItems.status !== 'loaded' || context.cartSummary.status !== 'loaded') return undefined
  if (!isVisibleUserCartMultiCart(context.cartSummary.data[cartId])) return undefined

  const cartItems = context.cartItems.data[cartId]
  const replacementHotels = newItems.filter(item => isInstantBookingLEHotelItem(item) || isBedbankItem(item))
  if (replacementHotels.length !== itemIds.length) return undefined

  const originalItems = itemIds.map(id => cartItems?.[id])
  const canReplace = originalItems.every(original => {
    if (!original || (original.state !== 'active' && original.state !== 'save_for_later')) return false
    const item = original.item
    if (!isInstantBookingLEHotelItem(item) && !isBedbankItem(item)) return false
    return replacementHotels.every(replacement => replacement.offerId === item.offerId && replacement.itemType === item.itemType)
      && !newItems.some(replacement => replacement.itemId === item.itemId)
  })
  if (!canReplace) return undefined

  return {
    cartId,
    itemIds,
    currentStates: Object.fromEntries(originalItems.map(item => [item.id, item.state])),
  }
}

export function clearCartAvailabilityReplacement(search: string): string {
  const query = deleteSearchParams(search, REPLACE_CART_ITEM_ID)
  return query ? `?${query}` : ''
}
